import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Search, DollarSign, TrendingUp, AlertTriangle, Zap, Filter,
  ArrowUpRight, ChevronRight, Sparkles,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

const PRIORITY_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' };
const CATEGORY_COLORS: Record<string, string> = {
  'Asset Write-off': '#3b82f6', 'Superannuation': '#8b5cf6', 'Director Bonus': '#f59e0b',
  'Trust Distribution': '#ec4899', 'Loss Carry-back': '#ef4444', 'Prepaid Expenses': '#06b6d4',
  'Bad Debts': '#f97316', 'Missing Deductions': '#ef4444', 'Vehicle': '#22c55e',
  'Tax Offset': '#6366f1', 'Medicare Levy Surcharge': '#14b8a6',
};

function fmtCurrency(v: number): string {
  if (v >= 0) return `$${v.toLocaleString()}`;
  return `-$${Math.abs(v).toLocaleString()}`;
}

export default function OpportunityScannerPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['opportunity-scan'],
    queryFn: async () => {
      const r = await apiClient.get('/opportunities/scan');
      return r.data.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 mins (scan is expensive)
  });

  const opportunities = data?.opportunities || [];
  const categories = Object.keys(data?.byCategory || {});

  const filtered = opportunities.filter((o: any) => {
    if (filterPriority !== 'all' && o.priority !== filterPriority) return false;
    if (filterCategory !== 'all' && o.category !== filterCategory) return false;
    if (search) {
      const s = search.toLowerCase();
      return o.clientName?.toLowerCase().includes(s) || o.title?.toLowerCase().includes(s) || o.category?.toLowerCase().includes(s);
    }
    return true;
  });

  // Chart data
  const categoryData = Object.entries(data?.byCategory || {})
    .map(([name, val]: [string, any]) => ({ name, saving: val.saving, count: val.count, fill: CATEGORY_COLORS[name] || '#6b7280' }))
    .sort((a, b) => b.saving - a.saving);

  const priorityData = [
    { name: 'High', value: data?.highPriority || 0, fill: PRIORITY_COLORS.high },
    { name: 'Medium', value: data?.mediumPriority || 0, fill: PRIORITY_COLORS.medium },
    { name: 'Low', value: data?.lowPriority || 0, fill: PRIORITY_COLORS.low },
  ].filter(d => d.value > 0);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <p className="text-sm text-muted-foreground">Scanning all connected clients for tax opportunities...</p>
        <p className="text-xs text-muted-foreground">This may take a minute</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Sparkles}
        title="Tax Opportunity Scanner"
        description={`Auto-detected tax saving opportunities across ${data?.totalClients || 0} connected clients`}
        iconColor="bg-yellow-500/10 text-yellow-500"
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1">
            <Search className="h-3.5 w-3.5" /> Re-scan
          </Button>
        }
      />


      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-muted-foreground uppercase">Clients Scanned</p>
            <p className="text-lg font-bold">{data?.totalClients || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-muted-foreground uppercase">Opportunities Found</p>
            <p className="text-lg font-bold text-blue-600">{data?.totalOpportunities || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-green-500/30">
          <CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-green-500 uppercase">Total Potential Savings</p>
            <p className="text-lg font-bold text-green-500">{fmtCurrency(data?.totalEstimatedSaving || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-red-500/30">
          <CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-red-500 uppercase">High Priority</p>
            <p className="text-lg font-bold text-red-500">{data?.highPriority || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-muted-foreground uppercase">Avg per Client</p>
            <p className="text-lg font-bold">{fmtCurrency(Math.round((data?.totalEstimatedSaving || 0) / Math.max(data?.totalClients || 1, 1)))}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categoryData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Savings by Category</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(180, categoryData.length * 30)}>
                <BarChart data={categoryData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#888' }} tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#999' }} width={120} />
                  <Tooltip cursor={false} contentStyle={{ backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }} itemStyle={{ color: '#94a3b8' }} wrapperStyle={{ outline: 'none' }} formatter={(v: number) => [fmtCurrency(v), 'Saving']} />
                  <Bar dataKey="saving" radius={[0, 4, 4, 0]} activeBar={false}>
                    {categoryData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
        {priorityData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Priority Distribution</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={priorityData} dataKey="value" nameKey="name" cx="35%" cy="50%" outerRadius={65} innerRadius={30} activeShape={null as any}>
                    {priorityData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip cursor={false} contentStyle={{ backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }} itemStyle={{ color: '#94a3b8' }} wrapperStyle={{ outline: 'none' }} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Opportunities List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">All Opportunities ({filtered.length})</CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {['all', 'high', 'medium', 'low'].map(f => (
                  <button key={f} onClick={() => setFilterPriority(f)}
                    className={cn('px-2 py-0.5 text-[10px] rounded-md font-medium capitalize', filterPriority === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80')}>
                    {f}
                  </button>
                ))}
              </div>
              {categories.length > 0 && (
                <select
                  value={filterCategory}
                  onChange={e => setFilterCategory(e.target.value)}
                  className="h-6 text-[10px] rounded-md border bg-background px-1"
                >
                  <option value="all">All Categories</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
              <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="w-40 h-6 text-xs" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {filtered.map((opp: any, i: number) => (
              <div
                key={i}
                className="flex items-start gap-3 p-3 hover:bg-muted/30 cursor-pointer transition-colors"
                onClick={() => navigate(`/panel/files/clients/${opp.clientId}`)}
              >
                {/* Priority indicator */}
                <div className={cn('h-2 w-2 rounded-full mt-1.5 flex-shrink-0',
                  opp.priority === 'high' ? 'bg-red-500' : opp.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                )} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold">{opp.clientName}</span>
                    <Badge variant="outline" className="text-[9px] capitalize">{opp.entityType}</Badge>
                    <Badge style={{ backgroundColor: (CATEGORY_COLORS[opp.category] || '#6b7280') + '20', color: CATEGORY_COLORS[opp.category] || '#6b7280', border: 'none' }} className="text-[9px]">
                      {opp.category}
                    </Badge>
                    {opp.deadline && (
                      <Badge variant="destructive" className="text-[9px]">Due: {opp.deadline}</Badge>
                    )}
                  </div>
                  <p className="text-xs font-medium">{opp.title}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{opp.description}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                      <Zap className="h-3 w-3" /> {opp.action.substring(0, 80)}{opp.action.length > 80 ? '...' : ''}
                    </span>
                  </div>
                </div>

                {/* Estimated saving */}
                <div className="text-right flex-shrink-0">
                  {opp.estimatedSaving > 0 ? (
                    <p className="text-sm font-bold text-green-500">{fmtCurrency(opp.estimatedSaving)}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Review</p>
                  )}
                  <p className="text-[10px] text-muted-foreground">est. saving</p>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-8 text-center text-muted-foreground text-xs">
                {opportunities.length === 0 ? 'No opportunities found. Connect clients to accounting software to enable scanning.' : 'No opportunities match your filters.'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
